import React from "react";
import { ChevronRight } from "lucide-react";
import styled, { css, useTheme } from "styled-components";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import {
  ClientStatus,
  LanguageNameSpaces,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { INVITATION_PERMISSION_TO_DISPLAY } from "features/configuration/common/constants.ts";
import Text from "common/Typography/Text";
import { ApiOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import Flex from "common/Boxes/Flex";
import { UserGroup } from "features/users-management/types";
import { LARGE_MOBILE } from "common/constants";
import RowActions, {
  RowActionsType,
} from "features/users-management/RowActions";
import ApiSlotRow from "features/users-management/ApiSlotRow";
import { hexToRGB } from "@shared/utils/misc";

interface UserRowProps {
  user: UserGroup;
  isExpanded: boolean;
  onToggle: () => void;
  accountBalances: Record<number, number>;
  isPortfolioLoading: boolean;
}

const UserRow: React.FC<UserRowProps> = ({
  user,
  isExpanded,
  onToggle,
  accountBalances,
  isPortfolioLoading,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.USERS_MANAGEMENT);
  const { isNewLook } = useTheme();
  const permission =
    INVITATION_PERMISSION_TO_DISPLAY[
      user.sub_user_permission_level ?? UserImpersonationPermissionLevel.NONE
    ];

  const activeSlotsCount = user.apiSlots.filter((slot) =>
    slot.accounts.some(
      (account) => account.client_status === ClientStatus.ACTIVE
    )
  ).length;

  // New look: one 56px row - the user (name over UID and access), their
  // balance over the slots they use on the right - that opens a well with
  // their API slots and accounts.
  if (isNewLook) {
    const balances = user.apiSlots
      .flatMap((slot) => slot.accounts)
      .map((account) => accountBalances?.[account.client_id])
      .filter((balance): balance is number => balance !== undefined);
    const total = balances.reduce((sum, balance) => sum + balance, 0);
    const meta = [
      user.raw_sub_user_uid ? `UID: ${user.raw_sub_user_uid}` : undefined,
      permission,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <div role="listitem">
        <NewLookRow
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          $isExpanded={isExpanded}
          onClick={onToggle}
          onKeyDown={(event) => {
            if (
              event.target === event.currentTarget &&
              (event.key === "Enter" || event.key === " ")
            ) {
              event.preventDefault();
              onToggle();
            }
          }}
        >
          <NewLookChevron size={16} strokeWidth={1.75} $expanded={isExpanded} />
          <NewLookAvatar aria-hidden={true}>
            {user.sub_user_invite_prefix[0]}
          </NewLookAvatar>
          <NewLookNames>
            <NewLookName title={user.sub_user_invite_prefix}>
              {user.sub_user_invite_prefix}
            </NewLookName>
            <NewLookMeta>{meta}</NewLookMeta>
          </NewLookNames>
          <NewLookEnd>
            {isPortfolioLoading ? (
              <Skeleton width={72} height={14} />
            ) : (
              <NewLookFigure>
                {balances.length
                  ? `$${getLocaleStringWithPrecision(total, 2)}`
                  : "—"}
              </NewLookFigure>
            )}
            <NewLookMeta>
              {t("api_slots_count", { count: activeSlotsCount })}
            </NewLookMeta>
          </NewLookEnd>
          <RowActions type={RowActionsType.USER} user={user} />
        </NewLookRow>
        {isExpanded && (
          <NewLookWell>
            {user.apiSlots.map((slot, index) => (
              <ApiSlotRow
                key={index}
                slot={slot}
                accountBalances={accountBalances}
                isPortfolioLoading={isPortfolioLoading}
              />
            ))}
          </NewLookWell>
        )}
      </div>
    );
  }

  return (
    <UserRowWrapper $isExpanded={isExpanded}>
      <UserMainRow onClick={onToggle}>
        <RowLeft>
          {isExpanded ? (
            <DownOutlined style={{ fontSize: 12, cursor: "pointer" }} />
          ) : (
            <RightOutlined style={{ fontSize: 12, cursor: "pointer" }} />
          )}
          <UserAvatar>{user.sub_user_invite_prefix[0]}</UserAvatar>
          <UserInfoContainer>
            <UserName $weight={500} $ellipsis>
              {user.sub_user_invite_prefix}
            </UserName>
            {!!user.raw_sub_user_uid && (
              <UserEmail type="secondary" $ellipsis style={{ marginTop: 2 }}>
                UID: {user.raw_sub_user_uid.toString()}
              </UserEmail>
            )}
            <UserEmail type="secondary" $ellipsis style={{ marginTop: 2 }}>
              {permission}
            </UserEmail>
          </UserInfoContainer>
        </RowLeft>

        <RowRight>
          <Flex $alignItems="center">
            <StyledApiIcon />
            <SlotStats type="secondary">
              {activeSlotsCount}{" "}
              {activeSlotsCount === 1 ? t("slot") : t("slots")}
            </SlotStats>
          </Flex>
          <RowActions type={RowActionsType.USER} user={user} />
        </RowRight>
      </UserMainRow>

      {isExpanded && (
        <UserExpandedContent>
          {user.apiSlots.map((slot, index) => (
            <ApiSlotRow
              key={index}
              slot={slot}
              accountBalances={accountBalances}
              isPortfolioLoading={isPortfolioLoading}
            />
          ))}
        </UserExpandedContent>
      )}
    </UserRowWrapper>
  );
};

export default UserRow;

/** New look: a loading row in the shape of a user row. */
export const UserRowSkeleton = () => (
  <NewLookRow as="div" $isExpanded={false} $static={true}>
    <span />
    <Skeleton width={32} height={32} radius={16} />
    <NewLookNames>
      <Skeleton width={120} height={12} />
      <Skeleton width={160} height={10} />
    </NewLookNames>
    <NewLookEnd>
      <Skeleton width={72} height={12} />
      <Skeleton width={48} height={10} />
    </NewLookEnd>
    <span />
  </NewLookRow>
);

const UserRowWrapper = styled.div<{ $isExpanded: boolean }>`
  border-bottom: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  background: transparent;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      position: relative;
      border-bottom: 0;
      border-radius: 12px;
      /* The divider spans the content column, not the rows' hover inset. */
      &:not(:last-child)::after {
        content: "";
        position: absolute;
        left: 8px;
        right: 8px;
        bottom: 0;
        height: 1px;
        background: ${p.theme.colors.lightestBorderGrey};
      }
    `}
  ${(p) =>
    p.theme.isNewLook &&
    p.$isExpanded &&
    css`
      /* An open user reads as one tinted group: the user, then its slots. */
      margin: 4px 0;
      background: ${p.theme.colors.cardLightBgColor};
      &&::after {
        display: none;
      }
    `}
`;
const UserMainRow = styled.div`
  display: flex;
  padding: 20px 24px;
  align-items: center;
  cursor: pointer;
  min-height: 72px;
  column-gap: 12px;

  &:hover {
    background: ${(p) => hexToRGB(p.theme.colors.textColor, 0.02)};
  }

  @media (max-width: ${LARGE_MOBILE}) {
    padding: 16px 6px 16px 16px;
    min-height: auto;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      border-radius: 12px;
      /* && so the phone media query above doesn't win. */
      && {
        padding: 12px 8px;
        min-height: 64px;
      }
      &:hover {
        background: ${p.theme.colors.cardLightBgColor};
      }
    `}
`;
const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 12px;
    `}
`;
const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: ${(p) => p.theme.colors.primaryBlue};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;

  @media (max-width: 360px) {
    display: none;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      flex: none;
      width: 34px;
      height: 34px;
      color: ${p.theme.colors.primaryBlue};
      background: ${p.theme.colors.lightBlue};
      font-weight: ${p.theme.typography.fontWeights.BOLD};
    `}
`;
const UserName = styled(Text)`
  @media (max-width: ${LARGE_MOBILE}) {
    font-size: 13px;
  }
`;
const UserInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`;
const UserEmail = styled(Text)`
  font-size: 12px;
  width: 100%;
  max-width: 200px;

  @media (max-width: ${LARGE_MOBILE}) {
    max-width: 250px;
    font-size: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-variant-numeric: tabular-nums;
    `}
`;
const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 8px;
    align-items: center;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 20px;
    `}
`;
const SlotStats = styled(Text)`
  @media (max-width: 390px) {
    font-size: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      white-space: nowrap;
      font-variant-numeric: tabular-nums;
    `}
`;
const StyledApiIcon = styled(ApiOutlined)`
  color: ${(p) => p.theme.colors.primaryBlue};
  margin-right: 8px;

  @media (max-width: 450px) {
    display: none;
  }
`;
const UserExpandedContent = styled.div`
  background: ${(p) =>
    p.theme.isDarkMode
      ? p.theme.colors.layoutBgColor
      : p.theme.colors.statisticsBgColor};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      padding-bottom: 4px;
      background: transparent;
    `}
`;
const NewLookChevron = styled(ChevronRight)<{ $expanded: boolean }>`
  flex: none;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  transition: transform 0.2s ease;
  ${(p) =>
    p.$expanded &&
    css`
      transform: rotate(90deg);
    `}
`;
const NewLookRow = styled.div<{ $isExpanded: boolean; $static?: boolean }>`
  display: grid;
  grid-template-columns: 16px 32px minmax(0, 1fr) auto 32px;
  align-items: center;
  column-gap: 12px;
  min-height: 60px;
  padding: 8px 16px 8px 20px;
  outline: none;
  transition: background-color 120ms;
  ${(p) =>
    !p.$static &&
    css`
      cursor: pointer;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${getNewLookTokens(p.theme).focus};
      }
    `}
  [role="listitem"] + [role="listitem"] > &,
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  @media (max-width: 400px) {
    grid-template-columns: 16px minmax(0, 1fr) auto 32px;
    column-gap: 10px;
    padding-left: 16px;
    padding-right: 8px;
    > :nth-child(2) {
      display: none;
    }
  }
`;
const NewLookAvatar = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
`;
const NewLookNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;
const NewLookName = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookEnd = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
  text-align: right;
`;
const NewLookFigure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  line-height: 18px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
// The open user's slots and accounts, inset under its row.
const NewLookWell = styled.div`
  border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
`;
